import {
    EvaluationRange,
    SegmentEntityType,
    ViewRow,
    WpDisplayConfig,
    WpViewTable,
} from '../../core';
import { Row } from '../../grid';
import { TranslationKeyMap } from '../main.i18n';
import { RowValues } from '../model/row-values.model';

import { createDistributionData } from './create-distribution-data.function';
import { createDistributionTooltipData } from './create-distribution-tooltip-data.function';
import { createPercentageData } from './create-percentage-data.function';
import { determineRowLabel } from './determine-row-label.function';

export function setRowExpanded(r: Row, isExpanded: boolean): void {
    const expandIcon = isExpanded ? 'chevron-down' : 'chevron-right';
    const expandIconTooltip = isExpanded
        ? TranslationKeyMap._10092_COLLAPSE_ROW
        : TranslationKeyMap._10091_EXPAND_ROW;
    r.ctx.values.isExpanded = isExpanded;
    r.ctx.values.expandIcon = expandIcon;
    r.ctx.values.expandIconTooltip = expandIconTooltip;
}

export function createRowValues(
    r: ViewRow,
    table: WpViewTable,
    config: WpDisplayConfig,
    evalRanges: EvaluationRange[],
    selectedEvalRange: EvaluationRange,
): RowValues {
    const drillPath = config.drillPaths.find(
        (e) => e.identifier === table.drillPathIdentifier,
    );
    const segment = drillPath?.segments?.find(
        (e) => e.identifier === r.segmentIdentifier,
    );

    const canExpand =
        drillPath?.segments?.indexOf(segment) !==
        drillPath?.segments?.length - 1;

    const first = drillPath.segments[0];
    const last = drillPath.segments
        .slice()
        .reverse()
        .find((e) => e.entityType === SegmentEntityType.USER);
    const canLoadNewUser =
        segment.entityType === SegmentEntityType.USER &&
        segment !== first &&
        segment !== last;

    const indentPx = segment.index * 20 + (!canExpand ? 24 : 0);
    const distributionIndentPx = segment.index * 40 + 50;
    const showUserIcon = segment.entityType === SegmentEntityType.USER;

    let managerMatchesCurrentManager = true;
    if (segment.entityType === SegmentEntityType.USER) {
        managerMatchesCurrentManager =
            r.manager?.userId === r.currentManager?.userId;
    }

    let label = determineRowLabel(r, table, config);
    let labelTooltip: string | null = label;
    let showLabelTooltip = false;
    if (segment.entityType !== SegmentEntityType.USER && label?.length > 50) {
        labelTooltip = label;
        label = `${label?.slice(0, 47) ?? ''}...`;
        showLabelTooltip = true;
    }

    const winningPercentageData = createPercentageData(
        r.row,
        evalRanges,
        selectedEvalRange,
    );
    winningPercentageData.tooltipEnabled =
        segment.entityType !== SegmentEntityType.BEHAVIOUR &&
        segment.entityType !== SegmentEntityType.SUB_BEHAVIOUR;

    const pdRow = r.row.individual ?? r.row.peopleDetail;

    const distributionData = createDistributionData(pdRow);

    const distributionTooltipData = createDistributionTooltipData(pdRow);

    return {
        canExpand,
        canLoadNewUser,
        indentPx,
        distributionIndentPx,
        expandIcon: null,
        expandIconTooltip: null,
        showUserIcon,
        managerMatchesCurrentManager,
        label,
        labelTooltip,
        showLabelTooltip,
        winningPercentageData,
        distributionData,
        distributionTooltipData,
        assessedGoalCommitments: r.row.assessedGoalCommitments,
        excludedGoalCommitments: r.row.excludedGoalCommitments,
    };
}
